import React from "react";
import styled from "styled-components";

const skeletonLoading = `
  @keyframes skeleton-loading {
    0% {
      background-color: #f0f0f0;
    }
    100% {
      background-color: #e0e0e0;
    }
  }
`;

const Skeleton = styled.div`
  ${skeletonLoading}
  animation: skeleton-loading 1.5s infinite alternate;
  border-radius: 4px;
`;

const SkeletonContainer = styled.div`
  background: #f8f9fa;
  min-height: 100vh;
  padding: 32px 0;

  @media (max-width: 768px) {
    padding: 16px 0;
  }
`;

const SkeletonWrapper = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;

const SkeletonBackButton = styled(Skeleton)`
  width: 120px;
  height: 20px;
  margin-bottom: 24px;
`;

const SkeletonTitle = styled(Skeleton)`
  width: 200px;
  height: 32px;
  margin-bottom: 32px;
`;

const SkeletonInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  background: #f9f9f9;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 32px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 16px;
  }
`;

const SkeletonInfoBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SkeletonText = styled(Skeleton)`
  height: 12px;
  width: 100%;
  max-width: 100px;

  &.large {
    height: 20px;
    width: 100%;
    max-width: 150px;
  }
`;

const SkeletonSection = styled.div`
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const SkeletonSectionTitle = styled(Skeleton)`
  width: 150px;
  height: 20px;
  margin-bottom: 20px;
`;

const SkeletonTimeline = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  position: relative;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
`;

const SkeletonTimelineStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0 12px;

  @media (max-width: 768px) {
    padding: 0;
  }
`;

const SkeletonCircle = styled(Skeleton)`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  margin-bottom: 12px;

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    margin-bottom: 8px;
  }
`;

const SkeletonItemRow = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 16px;
  padding: 16px;
  background: #f9f9f9;
  border-radius: 6px;
  margin-bottom: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 80px 1fr;
    gap: 12px;
    padding: 12px;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const SkeletonItemImage = styled(Skeleton)`
  width: 100px;
  height: 100px;

  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
  }
`;

const SkeletonItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 8px;
`;

const SkeletonAddressSection = styled.div`
  background: #f9f9f9;
  border: 1px solid #eee;
  padding: 20px;
  border-radius: 6px;
`;

const SkeletonOrderDetailsSkeleton = () => {
  return (
    <SkeletonContainer>
      <SkeletonWrapper>
        <SkeletonBackButton />
        <SkeletonTitle />

        {/* Order Info Header */}
        <SkeletonInfoGrid>
          {Array.from({ length: 4 }).map((_, index) => (
            <SkeletonInfoBox key={index}>
              <SkeletonText />
              <SkeletonText className="large" />
            </SkeletonInfoBox>
          ))}
        </SkeletonInfoGrid>

        {/* Timeline */}
        <SkeletonSection>
          <SkeletonSectionTitle />
          <SkeletonTimeline>
            {Array.from({ length: 4 }).map((_, index) => (
              <SkeletonTimelineStep key={index}>
                <SkeletonCircle />
                <SkeletonText style={{ width: "100%" }} />
                <SkeletonText style={{ width: "80%", marginTop: "8px" }} />
              </SkeletonTimelineStep>
            ))}
          </SkeletonTimeline>
        </SkeletonSection>

        {/* Items Section */}
        <SkeletonSection>
          <SkeletonSectionTitle />
          {Array.from({ length: 2 }).map((_, index) => (
            <SkeletonItemRow key={index}>
              <SkeletonItemImage />
              <SkeletonItemInfo>
                <SkeletonText className="large" />
                <SkeletonText />
                <SkeletonText style={{ width: "120px" }} />
              </SkeletonItemInfo>
            </SkeletonItemRow>
          ))}
        </SkeletonSection>

        {/* Address Section */}
        <SkeletonSection>
          <SkeletonSectionTitle />
          <SkeletonAddressSection>
            <SkeletonText className="large" style={{ marginBottom: "12px" }} />
            <SkeletonText style={{ marginBottom: "8px" }} />
            <SkeletonText style={{ marginBottom: "8px" }} />
            <SkeletonText style={{ width: "150px" }} />
          </SkeletonAddressSection>
        </SkeletonSection>

        {/* Pricing Section */}
        <SkeletonSection>
          <SkeletonSectionTitle />
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "12px",
              }}
            >
              <SkeletonText style={{ width: "100px" }} />
              <SkeletonText style={{ width: "80px" }} />
            </div>
          ))}
        </SkeletonSection>
      </SkeletonWrapper>
    </SkeletonContainer>
  );
};

export default SkeletonOrderDetailsSkeleton;
